/**
 * Voltide presentation-layer formatting utilities.
 *
 * RULES:
 *  - These functions are presentation-only. They MUST NOT change any
 *    business calculation. Pass the raw computed number from the backend.
 *  - All money uses en-IN locale (lakh/crore grouping) via Intl.NumberFormat.
 *
 * Use across:
 *  - Rental Sandbox outputs panel
 *  - Proposal preview/export (server-rendered helpers in
 *    `services/proposal_engine_v2.py` mirror these conventions for parity).
 */

const INR_LOCALE = 'en-IN';

/**
 * Format INR amount in Indian style.
 *   formatINR(12345)              → "₹12,345"
 *   formatINR(12345.67, {decimals:2}) → "₹12,345.67"
 *   formatINR(15500000, {compact:true}) → "₹1.55 Cr"
 */
export function formatINR(x, { compact = false, decimals } = {}) {
  const n = Number(x);
  if (!Number.isFinite(n)) return '—';

  if (compact) {
    const abs = Math.abs(n);
    if (abs >= 1e7) return `${n < 0 ? '-' : ''}₹${(abs / 1e7).toFixed(2)} Cr`;
    if (abs >= 1e5) return `${n < 0 ? '-' : ''}₹${(abs / 1e5).toFixed(2)} L`;
    // fall through to standard for sub-lakh values
  }

  const d = decimals == null ? 0 : decimals;
  const formatted = new Intl.NumberFormat(INR_LOCALE, {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  }).format(n);
  return `₹${formatted}`;
}

/**
 * Format a raw kWh number with thousands grouping, 0 decimals by default.
 *   formatKWh(13927.79)            → "13,928 kWh"
 *   formatKWh(13927.79, {unit:false}) → "13,928"
 */
export function formatKWh(x, { decimals = 0, unit = true } = {}) {
  const n = Number(x);
  if (!Number.isFinite(n)) return '—';
  const formatted = new Intl.NumberFormat(INR_LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
  return unit ? `${formatted} kWh` : formatted;
}

/**
 * Format a raw kW number, 0 decimals.
 *   formatKW(250.4) → "250 kW"
 */
export function formatKW(x, { unit = true } = {}) {
  const n = Number(x);
  if (!Number.isFinite(n)) return '—';
  const formatted = new Intl.NumberFormat(INR_LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
  return unit ? `${formatted} kW` : formatted;
}

/**
 * Service-fee price displayed in ₹/kWh/mo style. 2 decimals always.
 *   formatPricePerKwhMonth(10.5232) → "₹10.52/kWh/mo"
 */
export function formatPricePerKwhMonth(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toFixed(2)}/kWh/mo`;
}

/**
 * Format a percentage value.
 *   formatPercent(0.234, {kind:'soc'})        → "23.4%"
 *   formatPercent(0.7,   {kind:'min_commit'}) → "70%"
 *
 * If `decimal` is true (default), input is in [0,1] and multiplied by 100
 * before display. Set decimal=false if the input is already a 0-100 number.
 */
export function formatPercent(x, { kind = 'soc', decimal = true } = {}) {
  const n = Number(x);
  if (!Number.isFinite(n)) return '—';
  const pct = decimal ? n * 100 : n;
  if (kind === 'min_commit') return `${Math.round(pct)}%`;
  // soc / soh: 1 decimal
  return `${pct.toFixed(1)}%`;
}

/**
 * Format hours with 1 decimal.
 *   formatHours(4.5) → "4.5 h"
 */
export function formatHours(x, { unit = true } = {}) {
  const n = Number(x);
  if (!Number.isFinite(n)) return '—';
  const s = n.toFixed(1);
  return unit ? `${s} h` : s;
}
